import { colors } from '@/styles';
import { useMemo } from 'react';
import { CellContent } from '../Table/blocks';
import { css } from '@emotion/react';
import orderStatusStrings from '@/constants/orderStatusStrings';

export const ORDER_STATUS_OPTIONS = [
    { label: 'Incomplete', value: 'incomplete' },
    { label: 'Pending', value: 'pending' },
    { label: 'Claimed', value: 'claimed' },
    { label: 'Open', value: 'open' },
    { label: 'In Progress', value: 'inProgress' },
    { label: 'Complete', value: 'complete' },
    { label: 'Cancelled', value: 'cancelled' },
    { label: 'Rejected', value: 'rejected' },
];

export const REPORT_TYPES = {
    MARKETPLACE_ORDERS_BILLING: 'MARKETPLACE_SHIPPER_BILLING',
};

export const REPORTS = {
    [REPORT_TYPES.MARKETPLACE_ORDERS_BILLING]: {
        label: 'Marketplace Orders - Billing',
        static: [
            {
                attribute: 'oms',
                comparator: '_eq',
                value: false,
            },
        ],
        parameters: [
            {
                label: 'Status',
                attribute: 'order_status',
                key: 'order_status',
                comparator: '_in',
                type: 'multiselect',
                required: false,
                modifier: (values) => {
                    return values.reduce((acc, value) => {
                        switch (value) {
                            case 'complete':
                                return [...acc, value, 'routedComplete'];
                            case 'inProgress':
                                return [...acc, value, 'routedInProgress'];
                            case 'incomplete':
                                return [...acc, value, 'pending_confirmation', 'processing', 'duplicate'];
                            default:
                                return [...acc, value];
                        }
                    }, []);
                },
            },
            {
                label: 'Shippers',
                attribute: 'shipper_id',
                key: 'shipper_id',
                comparator: '_in',
                type: 'multiselect',
                required: false,
            },
            {
                label: 'Billing Start Date',
                attribute: 'billing_completion_time',
                key: 'billing_start',
                comparator: '_gt',
                type: 'date',
                required: true,
            },
            {
                label: 'Billing End Date',
                attribute: 'billing_completion_time',
                key: 'billing_end',
                comparator: '_lte',
                type: 'date',
                required: true,
            },
        ],
        getRowId: (row) => row.order_id,
        columns: [
            {
                Header: 'Order #',
                id: 'order_number',
                width: 125,
                disableSortBy: false,
                accessor: (order) => order.order_number,
                Cell: ({ value }) => {
                    return <CellContent>{value}</CellContent>;
                },
                footerAccessor: (order) => '',
                Footer: 'Totals',
            },
            {
                Header: 'PO #',
                id: 'po_number',
                width: 125,
                disableSortBy: false,
                accessor: (order) => order.po_number,
                Cell: ({ value }) => {
                    return <CellContent>{value}</CellContent>;
                },
                footerAccessor: (order) => '',
                Footer: '',
            },
            {
                Header: 'Ref #',
                id: 'reference_id',
                width: 125,
                disableSortBy: false,
                accessor: (order) => order.reference_id,
                Cell: ({ value }) => {
                    return <CellContent>{value}</CellContent>;
                },
                footerAccessor: (order) => '',
                Footer: '',
            },
            {
                Header: 'Status',
                id: 'order_status',
                width: 125,
                disableSortBy: false,
                accessor: (order) => order.order_status,
                Cell: ({ value }) => {
                    return <CellContent>{orderStatusStrings[value] || value}</CellContent>;
                },
                footerAccessor: (order) => '',
                Footer: '',
            },
            {
                Header: 'Shipper Name',
                id: 'order_shipper.business_name',
                width: 250,
                disableSortBy: false,
                accessor: (order) => order.order_shipper?.business_name || 'N/A',
                Cell: ({ value }) => {
                    return <CellContent>{value}</CellContent>;
                },
                footerAccessor: (order) => '',
                Footer: '',
            },
            {
                Header: 'Carrier Name',
                id: 'order_carrier.business_name',
                width: 250,
                disableSortBy: false,
                accessor: (order) => order.order_carrier?.business_name || 'N/A',
                Cell: ({ value }) => {
                    return <CellContent>{value}</CellContent>;
                },
                footerAccessor: (order) => '',
                Footer: '',
            },
            {
                Header: 'FM Ship Rate',
                id: 'shipper_rate',
                width: 150,
                disableSortBy: false,
                accessor: (order) => order.shipper_rate || 0,
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.shipper_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.shipper_rate;
                        }, 0);
                    }, [rows]);

                    return <CellContent>{`$${total.toFixed(2)}`}</CellContent>;
                },
            },
            {
                Header: 'FM Carr Rate',
                id: 'carrier_rate',
                width: 150,
                disableSortBy: false,
                accessor: (order) => order.carrier_rate || 0,
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.carrier_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.carrier_rate;
                        }, 0);
                    }, [rows]);

                    return <CellContent>{`$${total.toFixed(2)}`}</CellContent>;
                },
            },
            {
                Header: 'FM Margin',
                id: 'margin',
                width: 150,
                disableSortBy: false,
                accessor: (order) => (order.shipper_rate || 0) - (order.carrier_rate || 0),
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.shipper_rate || 0) - (order.carrier_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.margin;
                        }, 0);
                    }, [rows]);

                    return (
                        <CellContent
                            css={css`
                                color: ${total >= 0 ? colors.greens.primary : colors.reds.primary};
                            `}
                        >{`$${total.toFixed(2)}`}</CellContent>
                    );
                },
            },
            {
                Header: 'MM Ship Rate',
                id: 'middle_mile_shipper_rate',
                width: 150,
                disableSortBy: false,
                accessor: (order) => order.middle_mile_shipper_rate || 0,
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.middle_mile_shipper_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.middle_mile_shipper_rate;
                        }, 0);
                    }, [rows]);

                    return <CellContent>{`$${total.toFixed(2)}`}</CellContent>;
                },
            },
            {
                Header: 'MM Carr Rate',
                id: 'middle_mile_carrier_rate',
                width: 150,
                disableSortBy: false,
                accessor: (order) => order.middle_mile_carrier_rate || 0,
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.middle_mile_carrier_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.middle_mile_carrier_rate;
                        }, 0);
                    }, [rows]);

                    return <CellContent>{`$${total.toFixed(2)}`}</CellContent>;
                },
            },
            {
                Header: 'MM Margin',
                id: 'middle_mile_margin',
                width: 150,
                disableSortBy: false,
                accessor: (order) => (order.middle_mile_shipper_rate || 0) - (order.middle_mile_carrier_rate || 0),
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.middle_mile_shipper_rate || 0) - (order.middle_mile_carrier_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.middle_mile_margin;
                        }, 0);
                    }, [rows]);

                    return (
                        <CellContent
                            css={css`
                                color: ${total >= 0 ? colors.greens.primary : colors.reds.primary};
                            `}
                        >{`$${total.toFixed(2)}`}</CellContent>
                    );
                },
            },
            {
                Header: 'Total Ship Rate',
                id: 'total_shipper_rate',
                width: 150,
                disableSortBy: false,
                accessor: (order) => (order.shipper_rate || 0) + (order.middle_mile_shipper_rate || 0),
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.shipper_rate || 0) + (order.middle_mile_shipper_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.total_shipper_rate;
                        }, 0);
                    }, [rows]);

                    return <CellContent>{`$${total.toFixed(2)}`}</CellContent>;
                },
            },
            {
                Header: 'Total Carr Rate',
                id: 'total_carrier_rate',
                width: 150,
                disableSortBy: false,
                accessor: (order) => (order.carrier_rate || 0) + (order.middle_mile_carrier_rate || 0),
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return sum + (order.carrier_rate || 0) + (order.middle_mile_carrier_rate || 0);
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.total_carrier_rate;
                        }, 0);
                    }, [rows]);

                    return <CellContent>{`$${total.toFixed(2)}`}</CellContent>;
                },
            },
            {
                Header: 'Total Margin',
                id: 'total_margin',
                width: 150,
                disableSortBy: false,
                accessor: (order) =>
                    (order.shipper_rate || 0) +
                    (order.middle_mile_shipper_rate || 0) -
                    (order.middle_mile_carrier_rate || 0) -
                    (order.carrier_rate || 0),
                Cell: ({ value }) => {
                    return <CellContent>{`$${value.toFixed(2)}`}</CellContent>;
                },
                footerAccessor: (orders) => {
                    return orders.reduce((sum, order) => {
                        return (
                            sum +
                            (order.shipper_rate || 0) +
                            (order.middle_mile_shipper_rate || 0) -
                            (order.middle_mile_carrier_rate || 0) -
                            (order.carrier_rate || 0)
                        );
                    }, 0);
                },
                Footer: ({ rows }) => {
                    const total = useMemo(() => {
                        return rows.reduce((sum, row) => {
                            return sum + row.values.total_margin;
                        }, 0);
                    }, [rows]);

                    return (
                        <CellContent
                            css={css`
                                color: ${total >= 0 ? colors.greens.primary : colors.reds.primary};
                            `}
                        >{`$${total.toFixed(2)}`}</CellContent>
                    );
                },
            },
        ],
    },
};
